import { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Sparkles, Download, FileText } from "lucide-react";

const ResumePreview = ({ d }) => (
  <div className="bg-white text-neutral-900 rounded-lg p-8 shadow-xl text-[13px] leading-relaxed max-h-[640px] overflow-y-auto" data-testid="resume-preview">
    <div className="text-2xl font-bold tracking-tight">{d.full_name}</div>
    <div className="text-[#8A1C1C] mt-0.5">{d.headline}</div>
    {d.contact && <div className="text-neutral-500 text-xs mt-1">{d.contact}</div>}
    <div className="h-px bg-[#8A1C1C] my-4" />
    {d.summary && <Sec t="Executive Summary"><p>{d.summary}</p></Sec>}
    {d.core_competencies?.length > 0 && <Sec t="Core Competencies"><p>{d.core_competencies.join("  ·  ")}</p></Sec>}
    {d.experience?.length > 0 && (
      <Sec t="Experience">
        {d.experience.map((x, i) => (
          <div key={i} className="mb-3">
            <div className="font-semibold">{x.title} — {x.org} <span className="text-neutral-500 font-normal text-xs ml-1">{x.period}</span></div>
            <ul className="list-disc ml-5">{(x.bullets || []).map((b, j) => <li key={j}>{b}</li>)}</ul>
          </div>
        ))}
      </Sec>
    )}
    {["leadership_principles", "board_and_advisory", "education"].map((k) =>
      d[k]?.length > 0 && <Sec key={k} t={k.replace(/_/g, " ").replace("and", "&")}><ul className="list-disc ml-5">{d[k].map((p, i) => <li key={i}>{p}</li>)}</ul></Sec>
    )}
  </div>
);
const Sec = ({ t, children }) => (
  <div className="mb-3"><div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#8A1C1C] mb-1">{t}</div>{children}</div>
);

export const ResumePdfBuilder = () => {
  const [target, setTarget] = useState("");
  const [ctx, setCtx] = useState("");
  const [resume, setResume] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => { api.get("/resume/mine").then((r) => { if (r.data[0]) { setResume(r.data[0]); setTarget(r.data[0].target_role || ""); } }).catch(() => {}); }, []);

  const build = async () => {
    setBusy(true);
    try { const { data } = await api.post("/resume/build", { target_role: target || null, context: ctx || null }); setResume(data); toast.success("Resume drafted"); }
    catch { toast.error("Generation failed, please try again"); }
    finally { setBusy(false); }
  };
  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/resume/${resume.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(resume.data.full_name || "resume").replace(/\s+/g, "-")}-Resume.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
    finally { setDownloading(false); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <Card className="p-6 space-y-4 lg:col-span-2 h-fit">
        <div className="label-caps flex items-center gap-1.5"><FileText className="h-3 w-3" />Executive resume · PDF</div>
        <p className="text-sm text-muted-foreground">Built from your profile, written in the language of the Tribunal library. Unknown metrics appear as [X%] for you to fill in.</p>
        <div><Label>Target role</Label><Input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="e.g. CTO at a Series B health-AI company" className="mt-1.5" data-testid="resume-pdf-target" /></div>
        <div><Label>Career highlights (optional)</Label><Textarea rows={6} value={ctx} onChange={(e) => setCtx(e.target.value)} placeholder="Past roles with dates, wins, team sizes, revenue, education…" className="mt-1.5" data-testid="resume-pdf-context" /></div>
        <Button onClick={build} disabled={busy} className="rounded-full w-full" data-testid="resume-pdf-generate">
          <Sparkles className="h-4 w-4 mr-1" />{busy ? "Drafting from the library…" : resume ? "Regenerate" : "Build my resume"}
        </Button>
      </Card>
      <div className="lg:col-span-3 space-y-3">
        {resume ? (
          <>
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground font-mono">Target · {resume.target_role}</div>
              <Button onClick={download} disabled={downloading} className="rounded-full" data-testid="resume-pdf-download"><Download className="h-4 w-4 mr-1" />{downloading ? "Preparing…" : "Download PDF"}</Button>
            </div>
            <ResumePreview d={resume.data} />
            {resume.sources?.length > 0 && <div className="text-xs text-muted-foreground font-mono" data-testid="resume-sources">Library sources: {resume.sources.map((s) => s.title).join(" · ")}</div>}
          </>
        ) : (
          <Card className="p-10 text-center text-muted-foreground text-sm" data-testid="resume-pdf-empty">Your resume preview appears here.</Card>
        )}
      </div>
    </div>
  );
};
